import { USER_ROLES } from '@/const/auth'
import { ROUTES } from '@/const/route'
import { useAuthStore } from '@/stores/authStore'
import { useUserContextStore } from '@/stores/userContextStore'

const defaultPathByRole = {
  [USER_ROLES.REACH]: `${ROUTES.BASE}${ROUTES.BRIEFS}`,
  [USER_ROLES.ORG_ADMIN]: `${ROUTES.BASE}${ROUTES.TICKETS}`,
  [USER_ROLES.CALL_CENTRE]: `${ROUTES.BASE}${ROUTES.CHECK_DELIVERY_ADDRESS}`,
  // Store customers have one screen, so it is also where they land.
  [USER_ROLES.HEAD_OFFICE]: `${ROUTES.BASE}${ROUTES.DELIVERY_REPORT}`,
  [USER_ROLES.STORE_OWNER]: `${ROUTES.BASE}${ROUTES.DELIVERY_REPORT}`,
  [USER_ROLES.STORE_MANAGER]: `${ROUTES.BASE}${ROUTES.DELIVERY_REPORT}`,
}

/**
 * Where to send someone whose role has no landing screen.
 *
 * A role that RUBIX does not map lands nowhere: the guard used to return
 * `defaultPathByRole[role]`, which is undefined for an unmapped role, and an
 * undefined return means "allow". The person was left on the base route with
 * no component while the sign-in spinner stayed up for good. Seen on
 * 2026-09-01 with a RUBIX role carrying one function this app does not know.
 *
 * This is not hypothetical for the pilot: the three store roles shipped with
 * no function mappings at all, and a mapping that is wrong on the day would
 * show an invited customer a permanent spinner rather than a way to ask for
 * help.
 */
const landingFor = role => defaultPathByRole[role] ?? { name: ROUTES.NO_PERMISSION }

/**
 * Navigation guard that checks if user has RUBIX app access
 * Redirects to no permission page if access is denied
 * Redirects authenticated users away from login page to their default route
 * @param {RouteLocationNormalized} to - Target route
 * @param {RouteLocationNormalized} from - Current route
 * @returns {NavigationGuardReturn} Navigation result or undefined to proceed
 */
export async function requiresAppAccess(to, from) {
  const authStore = useAuthStore()
  const userContextStore = useUserContextStore()

  const signedIn = await authStore.isUserSignedIn()

  if (!signedIn) {
    return undefined
  }

  try {
    // Check if permissions are loaded
    if (!userContextStore.isPermissionLoaded) {
      if (to.name !== ROUTES.NO_PERMISSION) {
        return { name: ROUTES.NO_PERMISSION }
      }
      return undefined
    }

    const userRole = userContextStore.roles[0]

    // Handle no permission page - redirect if user has access
    if (to.name === ROUTES.NO_PERMISSION) {
      // Only send them onward when their role actually has a screen to go to.
      // A role with no landing path belongs on this page, and redirecting it
      // back to itself aborts the navigation and strands the person.
      if (userContextStore.canAccessApp && defaultPathByRole[userRole]) {
        return defaultPathByRole[userRole]
      }
      return undefined
    }

    // Check app access for protected routes
    if (!userContextStore.canAccessApp) {
      return { name: ROUTES.NO_PERMISSION }
    }

    // Redirect from login page if already authenticated
    if (
      to.name === ROUTES.LOGIN ||
      to.name === `${ROUTES.LOGIN}/${ROUTES.REACH}`
    ) {
      if (
        to.query?.from &&
        to.query.from.startsWith('/') &&
        !to.query.from.startsWith('//')
      ) {
        return to.query.from
      }
      return landingFor(userRole)
    }

    // Redirect from base route to default route
    if (to.path === ROUTES.BASE) {
      return landingFor(userRole)
    }
  } catch (_) {
    if (to.name !== ROUTES.NO_PERMISSION) {
      return { name: ROUTES.NO_PERMISSION }
    }
  }

  return undefined
}
